import { z } from 'zod';

/** Shared transport limits (specification Section 7.1). */
export const LIMITS = {
  titleMax: 240,
  longTextBytes: 64 * 1024,
  summaryBytes: 8 * 1024,
  listMax: 100,
  requestBytes: 256 * 1024,
  pageDefault: 50,
  pageMax: 100,
  bulkMax: 100,
  mcpSearchHits: 20,
  mcpTimelineEntries: 20,
  mcpOutputBytes: 64 * 1024,
} as const;

const utf8Bytes = (value: string) => new TextEncoder().encode(value).length;

export const Uuid = z.uuid();
export const IsoDateTime = z.iso.datetime({ offset: true });
/** Calendar day without time of day, for example 2026-10-31. */
export const IsoDate = z.iso.date();
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Comma-separated ids (up to 20) in a list filter; one id is the simplest case. */
export const UuidList = z
  .string()
  .max(800)
  .refine((v) => v.split(',').every((id) => UUID_PATTERN.test(id.trim())) && v.split(',').length <= 20, { message: 'must be up to 20 comma-separated ids' });
/** Comma-separated people in a list filter: ids, "me" (the reader) and "none" (nobody). */
export const PersonList = z
  .string()
  .max(800)
  .refine((v) => v.split(',').every((id) => UUID_PATTERN.test(id.trim()) || id.trim() === 'me' || id.trim() === 'none') && v.split(',').length <= 20, { message: 'must be up to 20 comma-separated ids, me or none' });
export const SortDirection = z.enum(['asc', 'desc']);
export const Title = z
  .string()
  .min(1)
  .max(LIMITS.titleMax)
  .refine((v) => v.trim().length > 0, { message: 'must not be blank' });
export const LongMarkdown = z
  .string()
  .refine((v) => utf8Bytes(v) <= LIMITS.longTextBytes, { message: 'must be at most 64 KiB of UTF-8' });
export const SummaryMarkdown = z
  .string()
  .min(1)
  .refine((v) => utf8Bytes(v) <= LIMITS.summaryBytes, { message: 'must be at most 8 KiB of UTF-8' });
export const ShortText = z.string().max(2000);
export const Reason = z.string().min(1).max(2000).refine((v) => v.trim().length > 0, { message: 'must not be blank' });
export const Version = z.number().int().min(1);
/** BCP 47 language tag of authored content, including "und" for unknown. */
export const ContentLocale = z
  .string()
  .regex(/^(und|[a-z]{2,3}(-[A-Za-z0-9]{2,8})*)$/, 'must be a BCP 47 language tag');
export const RequestKey = z.string().regex(/^REQ-\d+$/);
export const WorkKey = z.string().regex(/^VAM-\d+$/);
export const DisplayKey = z.string().regex(/^(REQ|VAM)-\d+$|^[A-Z][A-Z0-9_]+-\d+$/);

export const boundedList = <T extends z.ZodType>(item: T, max: number = LIMITS.listMax) => z.array(item).max(max);

export const ErrorCode = z.enum([
  'VALIDATION_FAILED',
  'MALFORMED_REQUEST',
  'UNAUTHENTICATED',
  'SESSION_EXPIRED',
  'FORBIDDEN',
  'NOT_FOUND',
  'VERSION_CONFLICT',
  'IDEMPOTENCY_CONFLICT',
  'RETRY_IN_PROGRESS',
  'STALE_GENERATION',
  'INVALID_STATE',
  'GUARD_VIOLATION',
  'RATE_LIMITED',
  'CONNECTOR_UNAVAILABLE',
  'CAPABILITY_UNAVAILABLE',
  'PAYLOAD_TOO_LARGE',
  'CSRF_REJECTED',
  'INTERNAL_ERROR',
]);
export type ErrorCode = z.infer<typeof ErrorCode>;

export const FieldIssue = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});
export type FieldIssue = z.infer<typeof FieldIssue>;

export const ErrorEnvelope = z.object({
  error: z.object({
    code: ErrorCode,
    message: z.string(),
    details: z
      .object({
        fields: z.array(FieldIssue).optional(),
        violations: z.array(z.object({ code: z.string(), message: z.string() })).optional(),
        currentVersion: z.number().int().optional(),
        currentGeneration: z.number().int().optional(),
        retryAfterSeconds: z.number().int().optional(),
      })
      .optional(),
  }),
  meta: z.object({ correlationId: z.string() }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelope>;

export const Meta = z.object({
  correlationId: z.string(),
  asOf: IsoDateTime.optional(),
});

/** total counts every matching row; lists send it with the first page only. */
export const CollectionMeta = Meta.extend({ nextCursor: z.string().nullable(), total: z.number().int().optional() });

export const envelope = <T extends z.ZodType>(data: T) => z.object({ data, meta: Meta });
export const collection = <T extends z.ZodType>(item: T) => z.object({ data: z.array(item), meta: CollectionMeta });

export const PageQuery = z.object({
  cursor: z.string().max(512).optional(),
  limit: z.string().regex(/^\d{1,3}$/).optional(),
});

export const UserRef = z.object({
  id: Uuid,
  displayName: z.string(),
  email: z.string().nullable(),
  status: z.string(),
});
export type UserRef = z.infer<typeof UserRef>;

export const IdempotencyKeyHeader = z.string().min(8).max(200).regex(/^[A-Za-z0-9._:-]+$/);
